/**
 * @documental
 * @export RadioGroup
 */
import { ReactNode, forwardRef } from "react";

import { withSubComponents } from "../../utilities/withSubComponents";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { Field } from "./Field";
import { Input } from "./Input";
import { Option } from "./Option";
import { Placeholder } from "./Placeholder";

/**
 * The `RadioGroup` component properties.
 */
export interface RadioGroupProps extends InputBaseProps<string | number | boolean | null> {
  /**
   * The options to display.
   */
  readonly children?: ReactNode;

  /**
   * Display options in a horizontal layout.
   * Value can be "vertical" or "horizontal".
   * Defaults to "vertical" if no value is supplied.
   */
  readonly orientation?: "vertical" | "horizontal";

  /**
   * Remove padding from the radio group.
   */
  readonly unpadded?: boolean;
}

/**
 * The `RadioGroup` component sub-components.
 */
export interface RadioGroupSubComponents {
  /**
   * Represents a single option which can be placed within the RadioGroup component.
   */
  readonly Option: typeof Option;

  /**
   * A React Hook Form compatible RadioGroup component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the RadioGroup which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * A form input that presents a group of series of radio buttons which can be toggled.
 *
 * `import { RadioGroup } from "./RadioGroup";`
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<RadioGroup.Field .../>`
 *
 * <strong>To use the regular version</strong>
 *
 * `<RadioGroup .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
  const [value, onChange] = useSimpleInputState<string | null>(null);

  const handleChange = (newValue: string | null, prev: string | null) => {
    onChange(newValue, prev);
  };

  return (
    <RadioGroup label="Regular Radio Group" onChange={handleChange} value={value} {...props}>
      <RadioGroup.Option value="radio1">Regular Radio 1</RadioGroup.Option>
      <RadioGroup.Option value="radio2">Regular Radio 2</RadioGroup.Option>
      <RadioGroup.Option value="radio3">Regular Radio 3</RadioGroup.Option>
    </RadioGroup>
  );
  ```
 */
export const RadioGroup = withSubComponents<RefComponent<HTMLInputElement, RadioGroupProps>, RadioGroupSubComponents>(
  {
    Option,
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, RadioGroupProps>(function RadioGroup(props, ref) {
    const { children } = props;

    return (
      <Input {...props} ref={ref}>
        {children}
      </Input>
    );
  }),
);
